<script setup lang="ts">
import { onBeforeUnmount, onMounted, useTemplateRef } from 'vue';
import exposeToGlobalScope from '@/shared/utils/expose-to-global-scope';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import UploadedPhotoItem from './uploaded-photos/UploadedPhotoItem.vue';
import {
  type UploadedPhotoItemHandle,
  type UploadedPhotosSidebarController,
  type UploadedSidebarPhoto,
  useUploadedPhotosSidebar,
} from './uploaded-photos/useUploadedPhotosSidebar';

interface UploadedPhotosSidebarPublicApi {
  addToQueue: (photos: UploadedSidebarPhoto[]) => void;
  readonly animatedPhotoUuids: string[];
  readonly containerOffsetTop: number;
  readonly displayedPhotos: UploadedSidebarPhoto[];
  readonly extraPhotoCount: number;
  hasPhotoAnimated: (photo: UploadedSidebarPhoto) => boolean;
  readonly haveAnyPhotosAnimated: boolean;
  readonly isAnimating: boolean;
  readonly isContainerOffsetTopTransitionEnabled: boolean;
  readonly isExtraPhotoCounterVisible: boolean;
  isPhotoAnimating: (photo: UploadedSidebarPhoto) => boolean;
  lazyAnimateQueue: (isContinuing?: boolean) => Promise<void>;
  onPhotoUpload: (photos: UploadedSidebarPhoto[]) => void;
  readonly queue: UploadedSidebarPhoto[];
  updateContainerOffsetTop: () => Promise<void>;
}

const props = withDefaults(
  defineProps<{
    fade?: boolean;
    watch?: boolean;
  }>(),
  {
    fade: false,
    watch: false,
  }
);

const emit = defineEmits<{
  animationComplete: [];
}>();

const container = useTemplateRef<HTMLElement>('container');
const extraPhotoCounter = useTemplateRef<HTMLElement>('extraPhotoCounter');
const photoItems = useTemplateRef<UploadedPhotoItemHandle[]>('photoItems');

const sidebar = useUploadedPhotosSidebar({
  getContainer: () => container.value,
  getExtraPhotoCounter: () => extraPhotoCounter.value,
  getPhotoItems: () => photoItems.value ?? [],
  onAnimationComplete: () => emit('animationComplete'),
  watchUploads: () => props.watch,
});

const {
  addToQueue,
  animatedPhotoUuids,
  containerOffsetTop,
  displayedPhotos,
  extraPhotoCount,
  hasPhotoAnimated,
  haveAnyPhotosAnimated,
  isAnimating,
  isContainerOffsetTopTransitionEnabled,
  isExtraPhotoCounterVisible,
  isPhotoAnimating,
  lazyAnimateQueue,
  onPhotoUpload,
  queue,
  updateContainerOffsetTop,
}: UploadedPhotosSidebarController = sidebar;

const publicApi: UploadedPhotosSidebarPublicApi = {
  addToQueue,
  get animatedPhotoUuids(): string[] {
    return animatedPhotoUuids.value;
  },
  get containerOffsetTop(): number {
    return containerOffsetTop.value;
  },
  get displayedPhotos(): UploadedSidebarPhoto[] {
    return displayedPhotos.value;
  },
  get extraPhotoCount(): number {
    return extraPhotoCount.value;
  },
  hasPhotoAnimated,
  get haveAnyPhotosAnimated(): boolean {
    return haveAnyPhotosAnimated.value;
  },
  get isAnimating(): boolean {
    return isAnimating.value;
  },
  get isContainerOffsetTopTransitionEnabled(): boolean {
    return isContainerOffsetTopTransitionEnabled.value;
  },
  get isExtraPhotoCounterVisible(): boolean {
    return isExtraPhotoCounterVisible.value;
  },
  isPhotoAnimating,
  lazyAnimateQueue,
  onPhotoUpload,
  get queue(): UploadedSidebarPhoto[] {
    return queue.value;
  },
  updateContainerOffsetTop,
};

defineExpose(publicApi);

onMounted(() => {
  exposeToGlobalScope(publicApi, 'UploadedPhotosSidebar');
  sidebar.enter();
});

onBeforeUnmount(sidebar.leave);
</script>

<template>
  <div
    class="UploadedPhotosSidebar__fade"
    :class="{
      'UploadedPhotosSidebar__fade--active':
        fade && haveAnyPhotosAnimated,
    }"
  />

  <div
    ref="container"
    class="UploadedPhotosSidebar"
    data-testid="uploaded-photos-sidebar"
    :class="{
      'UploadedPhotosSidebar--offset-top-transition-enabled':
        isContainerOffsetTopTransitionEnabled,
    }"
    :style="{ top: `${containerOffsetTop}px` }"
  >
    <div
      class="UploadedPhotosSidebar__title opacity-0"
      :class="{ 'opacity-100': haveAnyPhotosAnimated }"
    >
      {{ $t('UploadedPhotosSidebar.title') }}
    </div>

    <ul class="UploadedPhotosSidebar__list">
      <UploadedPhotoItem
        v-for="photo of displayedPhotos"
        ref="photoItems"
        :key="photo.uuid"
        :photo="photo"
        :is-animating="isPhotoAnimating(photo)"
        :has-animated="hasPhotoAnimated(photo)"
      />

      <li
        ref="extraPhotoCounter"
        class="UploadedPhotosSidebar__extra-photo-counter opacity-0"
        data-testid="uploaded-photos-extra-count"
        :class="{ 'opacity-100': isExtraPhotoCounterVisible }"
      >
        {{ $t('UploadedPhotosSidebar.count', { extraPhotoCount }) }}
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.UploadedPhotosSidebar {
  margin-right: 1.7rem;
  position: absolute;
  right: 0;

  &--offset-top-transition-enabled {
    transition: top 300ms;
  }
}

.UploadedPhotosSidebar__fade {
  position: absolute;
  top: 0;
  min-height: var(--Global--height-landscape);
  right: 0;
  width: 15rem;
  pointer-events: none;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    transform: translateX(60%);
    inset: 0;
    border-radius: 100%;
    background: #000000;
    filter: blur(4rem);
    opacity: 0;
    transition: opacity 300ms;
  }

  &--active::after {
    opacity: 1;
  }
}

.UploadedPhotosSidebar__title {
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  text-align: center;
  font-size: 1.4rem;
  line-height: 1.1;
  max-width: min-content;
  margin: 0 auto -0.4rem;
  transition: opacity 300ms;
}

.UploadedPhotosSidebar__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.UploadedPhotosSidebar__extra-photo-counter {
  text-transform: var(--Kiosk--font_capitalize, uppercase);
  text-align: center;
  font-size: 1.4rem;
  line-height: 1.1;
  white-space: nowrap;
  width: min-content;
  transition: opacity 300ms;
  margin: 1.6rem auto 0;
}
</style>
